import type { DocumentDTO } from '@almanac/shared';
import { formatRowDate } from '../lib/documents.js';

/**
 * One document row in the index / drill-down lists. A small file-tile, the
 * title (with a "Vital" pill and a paperclip when a file is attached), an
 * uppercase meta line, and a right-aligned date. The date is the
 * document's filed date, or — when it's expiring/expired — its expiry in
 * the accent colour, so the attention case stands out in any list.
 *
 * `meta` is supplied by the caller because it varies by context (a person
 * folder already says whose it is, a kind folder already says the kind).
 */
export function DocumentListItem({
  document: doc,
  meta,
  now,
  onOpen,
  dateOverride,
  trailing,
}: {
  document: DocumentDTO;
  meta: string;
  now: Date;
  onOpen: (document: DocumentDTO) => void;
  /** Replace the computed filed/expiry date on the right — the Vault
   *  shows last-confirmed / "review due" there instead. */
  dateOverride?: { label: string; warn: boolean };
  /** Replace the right slot entirely — the trip wallet puts its
   *  offline / vault flags there instead of a date. */
  trailing?: React.ReactNode;
}) {
  const date = dateOverride ?? formatRowDate(doc, now);
  const isVital = doc.documentCategory === 'vital';

  return (
    <button
      type="button"
      className="doc-row"
      onClick={() => onOpen(doc)}
      aria-label={`Open ${doc.title}`}
    >
      <span className="doc-row__icon" aria-hidden>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
          <path
            d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M14 3v5h5"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className="doc-row__mid">
        <span className="doc-row__title">
          {doc.title}
          {doc.starred && (
            <svg
              className="doc-row__star"
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-label="Key document"
            >
              <path d="M12 2l2.9 6.26 6.6.7-4.9 4.5 1.35 6.54L12 16.9 6.05 20l1.35-6.54-4.9-4.5 6.6-.7z" />
            </svg>
          )}
          {isVital && <span className="doc-row__pill">Vital</span>}
          {doc.fileName && (
            <svg
              className="doc-row__clip"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              aria-label="Has a file"
            >
              <path
                d="M21 11.5l-8.5 8.5a5 5 0 0 1-7-7l8.5-8.5a3.3 3.3 0 0 1 4.7 4.7l-8.5 8.5a1.6 1.6 0 0 1-2.3-2.3l7.8-7.8"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </span>
        {meta && <span className="doc-row__meta">{meta}</span>}
      </span>
      {trailing !== undefined ? (
        <span className="doc-row__date">{trailing}</span>
      ) : (
        <span className={`doc-row__date ${date.warn ? 'doc-row__date--warn' : ''}`}>{date.label}</span>
      )}
    </button>
  );
}
